import React, { useCallback } from "react";

import { LxTextInput } from "@labxchange/ui-components/lib/LxTextInput";

import { TableCellProps, CellChangeParams } from "../types";
import { useEditableCell } from "../hooks/useEditableCell";

export interface InputCellProps extends Pick<
  TableCellProps,
  | "cellId"
  | "rowIndex"
  | "colIndex"
  | "inputType"
  | "placeholder"
  | "value"
  | "disabled"
  | "showFeedback"
  | "isCorrect"
  | "feedback"
  | "unique"
  | "accuracy"
  | "colspan"
  | "suffix"
> {
  hint?: string;
  isPrediction?: boolean;
  isSubmitted?: boolean;
  isIncomplete?: boolean;
  onChange?: (params: Omit<CellChangeParams, "componentID">) => void;
}

export const InputCell: React.FC<InputCellProps> = ({
  cellId,
  rowIndex,
  colIndex,
  inputType = "text",
  placeholder,
  value,
  disabled,
  showFeedback,
  isCorrect,
  feedback,
  unique,
  colspan,
  hint,
  isPrediction,
  isSubmitted,
  isIncomplete,
  suffix,
  onChange,
}) => {
  const {
    cellClass,
    state,
    suffixIcon,
    translatedFeedback,
    translatedHint,
    shouldShowFeedback,
  } = useEditableCell({
    value,
    showFeedback,
    isCorrect,
    feedback,
    hint,
    isPrediction,
    isIncomplete,
  });

  const isPredictionSubmitted = isPrediction && isSubmitted;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // Keep value as string to preserve trailing zeros (e.g., "24.20")
      // Validation will convert to number for comparison
      const newValue = e.target.value === "" ? null : e.target.value;

      onChange?.({
        rowId: rowIndex,
        colId: colIndex,
        value: newValue,
        unique,
      });
    },
    [onChange, rowIndex, colIndex, unique]
  );

  return (
    <td
      key={cellId}
      className={cellClass}
      colSpan={colspan ? Number(colspan) : undefined}
    >
      <LxTextInput
        name={cellId}
        type={inputType === "num" ? "number" : inputType}
        placeholder={
          placeholder
            ? { id: placeholder, defaultMessage: placeholder }
            : undefined
        }
        value={value !== null && value !== undefined ? String(value) : ""}
        onChange={handleChange}
        disabled={disabled || isPredictionSubmitted}
        state={state}
        suffix={suffixIcon}
        suffixText={suffix}
        variant="borderless"
        feedback={translatedFeedback}
        showFeedback={shouldShowFeedback}
        hint={translatedHint}
        hintIconSize={24}
      />
    </td>
  );
};
